package ai.jessie.web.invoices

import ai.jessie.shared.invoices.InvoiceOptionsResponse
import ai.jessie.shared.invoices.InvoicePreviewResponse
import ai.jessie.shared.invoices.InvoiceResponse
import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.unavailableProblem
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.setValue
import kotlinx.coroutines.CancellationException
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.currentCoroutineContext
import kotlinx.coroutines.ensureActive
import kotlinx.coroutines.launch

data class InvoiceUiState(
    val invoices: List<InvoiceResponse>? = null,
    val current: InvoiceResponse? = null,
    val options: InvoiceOptionsResponse? = null,
    val draft: InvoiceDraft? = null,
    val line: InvoiceLineDraft? = null,
    val preview: InvoicePreviewResponse? = null,
    val busy: Boolean = false,
    val error: OnboardingProblem? = null,
    val problems: Map<String, String> = emptyMap(),
    val changes: List<String> = emptyList(),
    val notice: String? = null,
)

data class InvoiceActions(
    val refresh: () -> Unit,
    val create: () -> Unit,
    val changeDetails: ((InvoiceDraft) -> InvoiceDraft) -> Unit,
    val reloadChoices: () -> Unit,
    val startLine: (String) -> Unit,
    val changeLine: ((InvoiceLineDraft) -> InvoiceLineDraft) -> Unit,
    val addLine: () -> Unit,
    val discardLine: () -> Unit,
    val removeLine: (Int) -> Unit,
    val recalculate: () -> Unit,
    val confirm: () -> Unit,
    val cancel: () -> Unit,
    val close: () -> Unit,
    val withdraw: (OnboardingProblem) -> Unit,
    val progressed: (InvoiceResponse) -> Unit,
)

/**
 * UC-013 creation. Without [invoiceId] the state lists invoices and creates one Draft; with it the
 * state shows that invoice. Single-flight; a disposed page scope cannot publish late success, error
 * or finalisation, so a change of organisation never shows the previous tenant's answer. Every
 * amount comes from the server's preview; nothing is stored before [confirm].
 */
class InvoiceState(
    private val port: InvoicePort,
    private val scope: CoroutineScope,
    private val organisationId: String,
    private val invoiceId: String? = null,
) {
    var state by mutableStateOf(InvoiceUiState())
        private set

    val actions =
        InvoiceActions(
            refresh = ::refresh,
            create = ::create,
            changeDetails = { update -> draft { state.copy(draft = update(it)) } },
            reloadChoices = ::reloadChoices,
            startLine = { type -> draft { state.copy(line = InvoiceLineDraft(type)) } },
            changeLine = { update ->
                val line = state.line
                if (!state.busy && line != null) state = state.copy(line = update(line))
            },
            addLine = ::addLine,
            discardLine = {
                draft { state.copy(line = null, problems = emptyMap(), error = null) }
            },
            removeLine = { index ->
                draft { current ->
                    val lines = current.lines.filterIndexed { i, _ -> i != index }
                    state.copy(draft = current.copy(lines = lines), preview = null)
                }
                recalculate()
            },
            recalculate = ::recalculate,
            confirm = ::confirm,
            // A9: cancelling sends nothing and leaves all work eligible.
            cancel = {
                if (!state.busy)
                    state =
                        InvoiceUiState(
                            invoices = state.invoices,
                            notice =
                                "Abgebrochen. Es wurde nichts gespeichert und keine Arbeit " +
                                    "verrechnet.",
                        )
            },
            close = {
                if (!state.busy) {
                    state = InvoiceUiState()
                    refresh()
                }
            },
            // UC-015 A7: an artefact request lost access, so no invoice detail stays either.
            withdraw = { problem -> state = InvoiceUiState(busy = state.busy, error = problem) },
            // UC-014: the latest state of the opened invoice after progression or a rejection.
            progressed = { invoice ->
                if (state.current?.id == invoice.id) state = state.copy(current = invoice)
            },
        )

    fun refresh() {
        if (state.draft != null) return
        request {
            if (invoiceId == null) {
                val invoices = port.list(organisationId)
                currentCoroutineContext().ensureActive()
                state = state.copy(invoices = invoices)
            } else {
                val current = port.read(organisationId, invoiceId)
                currentCoroutineContext().ensureActive()
                state = state.copy(current = current)
            }
        }
    }

    // Steps 1-3: creation opens only with the eligible customers and proposals the server offers.
    private fun create() {
        if (invoiceId != null || state.draft != null) return
        request {
            val options = port.options(organisationId)
            currentCoroutineContext().ensureActive()
            state = state.copy(options = options, draft = options.newDraft())
        }
    }

    // Steps 4 and 6: a changed customer, currency or invoice date reloads the offered choices and,
    // when positions exist, recalculates them (B4).
    private fun reloadChoices() {
        val draft = state.draft ?: return
        request {
            val options = port.options(organisationId, draft.query())
            currentCoroutineContext().ensureActive()
            val proposed = draft.proposed(options)
            state =
                state.copy(
                    options = options,
                    draft = proposed,
                    line = state.line?.permitted(options),
                    preview = null,
                )
            if (proposed.lines.isNotEmpty()) calculate(proposed)
        }
    }

    // Steps 7-8 and A1: the position joins the draft and the server calculates every amount.
    private fun addLine() {
        val draft = state.draft
        val line = state.line
        if (state.busy || draft == null || line == null) return
        val unsendable = line.unsendableFields().mapValues { invoiceCorrection(it.key, it.value) }
        if (unsendable.isNotEmpty()) {
            state = state.copy(problems = unsendable, error = invoiceValidationProblem(unsendable))
            return
        }
        val extended = draft.copy(lines = draft.lines + line)
        state = state.copy(draft = extended, line = null)
        request { calculate(extended) }
    }

    private fun recalculate() {
        val draft = state.draft ?: return
        if (draft.lines.isEmpty()) return
        request { calculate(draft) }
    }

    private suspend fun calculate(draft: InvoiceDraft) {
        val preview = port.preview(organisationId, draft.request())
        currentCoroutineContext().ensureActive()
        state = state.copy(preview = preview, changes = emptyList())
    }

    // Steps 9-11: the reviewed totals travel with the confirmation; the server compares them (A7).
    private fun confirm() {
        val draft = state.draft?.takeIf { state.line == null }
        val preview = state.preview
        if (state.busy || draft == null || preview == null) return
        val unsendable = draft.unsendableFields().mapValues { invoiceCorrection(it.key, it.value) }
        if (unsendable.isNotEmpty()) {
            state = state.copy(problems = unsendable, error = invoiceValidationProblem(unsendable))
            return
        }
        request(saving = true) {
            val created = port.create(organisationId, draft.request(preview.totals))
            currentCoroutineContext().ensureActive()
            state =
                InvoiceUiState(
                    invoices = state.invoices,
                    current = created,
                    busy = true,
                    notice = "Rechnungsentwurf erstellt. Die ausgewählte Arbeit ist verrechnet.",
                )
        }
    }

    private inline fun draft(update: (InvoiceDraft) -> InvoiceUiState) {
        val draft = state.draft
        if (!state.busy && draft != null) state = update(draft)
    }

    private fun request(saving: Boolean = false, block: suspend () -> Unit) {
        if (state.busy) return
        state = state.copy(busy = true, error = null, problems = emptyMap(), notice = null)
        scope.launch {
            try {
                block()
            } catch (cancelled: CancellationException) {
                throw cancelled
            } catch (failure: InvoiceRequestFailure) {
                currentCoroutineContext().ensureActive()
                state = state.rejected(failure, saving)
            } catch (_: Exception) {
                currentCoroutineContext().ensureActive()
                // Only an unanswered creation leaves the stored state unknown; reads and previews
                // changed nothing.
                state =
                    state.copy(
                        preview = if (saving) null else state.preview,
                        error = if (saving) uncertainInvoiceProblem() else unavailableProblem(),
                    )
            } finally {
                currentCoroutineContext().ensureActive()
                state = state.copy(busy = false)
            }
        }
    }
}

private fun InvoiceDraft.query() =
    InvoiceOptionsQuery(
        customerId = customerId.takeIf { validInvoiceId(it) },
        currency = currency.ifBlank { null },
        issueDate = issueDate.ifBlank { null },
    )
